import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, any, filter, isNil, length, pipe, propEq, values} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {Carousel} from '../../components/Carousel/Carousel';
import {ListFeatures} from '../../components/ListFeatures/ListFeatures';
import {Section} from '../../components/Section/Section';
import {SectionStateFlagProps} from '../../components/SectionStateFlag/SectionStateFlag';
import {Separator} from '../../components/Separator/Separator';
import {FeaturesDataBundle} from '../../hooks/useGetDataForFeatures';
import {i18n} from '../../i18n/i18n';
import {getRecordsTranslate} from '../../utils/getRecordsTranslate';
import {isFeatureTested} from '../../utils/isFeatureTested';
import {EquipmentDocumentDownload} from './components/EquipmentDocumentDownload';

type FeaturesProps = Omit<FeaturesDataBundle, 'sectionConfig'>;
export function Features(props: FeaturesProps) {
  const hasFeatures = isNotNilOrEmpty(props.featuresData) && isNotNilOrEmpty(props.features);

  if (!hasFeatures && isNil(props.equipmentDocument)) {
    return null;
  }

  const listedFeatures = props.features ?? [];

  const isNotTested = !any(isFeatureTested, listedFeatures);
  const isDamaged = any((feature) => feature.variant === 'damaged', listedFeatures);

  const countDamages: (object: object) => number = pipe(
    values,
    filter(propEq('damaged', 'variant')),
    length
  );

  return (
    <Section
      id="EQUIPMENT"
      icon="features"
      heading={i18n.t('featuresHeader')}
      header={<EquipmentDocumentDownload equipmentDocument={props.equipmentDocument} />}
      content={
        <VStack spacing={[4, 6, 6, 10]}>
          <Show when={hasFeatures}>
            <ListFeatures
              columns={[1, 2, 2, 2]}
              spacing={[4, 6, 6, 10]}
              features={listedFeatures}
            />
          </Show>
          <Show when={props.featuresSlideshow && props.featuresSlideshow.length > 0}>
            <Separator />
            <Carousel isFilterOfDamageDisabled data={props.featuresSlideshow} />
          </Show>
        </VStack>
      }
      flag={match<[boolean, boolean], Nullish | SectionStateFlagProps>([isNotTested, isDamaged])
        .with(
          [false, true],
          always({severity: 'damage', text: getRecordsTranslate(countDamages(listedFeatures))})
        )
        .with([true, false], always(null))
        .otherwise(always({severity: 'good', text: i18n.t('sectionState.good')}))}
    />
  );
}
